import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { TCity, TCountry, TExpertiseItem, TProfession, TState } from '@onesource/schemas';
import { FC, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { MdOutlineViewModule, MdPerson } from 'react-icons/md';

import { ProDirectoryProfilePreviewFull } from './profile-preview-full';
import { SearchCardPreviewGrid } from './search-card-preview-grid';
import { normalizePhone, resolveExpertiseNames, TProFormValues } from './steps';

type Props = {
  countries: TCountry[];
  states: TState[];
  cities: TCity[];
  professions: TProfession[];
  expertiseItems: TExpertiseItem[];
};

type TPreviewView = 'profile' | 'card';

export const ProDirectoryStepPreview: FC<Props> = ({ countries, states, cities, professions, expertiseItems }) => {
  const { control } = useFormContext<TProFormValues>();
  const values = useWatch({ control });
  const [view, setView] = useState<TPreviewView>('profile');

  const professionOptions = (values.profession ?? []).map(id => ({
    id: Number(id),
    name: professions.find(p => String(p.id) === String(id))?.name ?? id,
  }));
  // Form state holds ids; the preview must show what the public profile will show.
  const expertiseNames = resolveExpertiseNames(values.expertise, expertiseItems);
  const businessCity = cities.find(c => c.id === values.businessCityId)?.name ?? null;
  const businessState = states.find(s => s.id === values.businessStateId)?.name ?? null;
  const businessCountry = countries.find(c => c.id === values.businessCountryId)?.name ?? null;

  return (
    <Box>
      <Box
        sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}
      >
        <Typography variant="h6" fontWeight={700}>
          Profile preview
        </Typography>
        <ToggleButtonGroup
          value={view}
          exclusive
          size="small"
          onChange={(_, next: TPreviewView | null) => next && setView(next)}
        >
          <ToggleButton value="profile">
            <MdPerson style={{ marginRight: 6 }} />
            Profile view
          </ToggleButton>
          <ToggleButton value="card">
            <MdOutlineViewModule style={{ marginRight: 6 }} />
            Search card
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {view === 'profile' ? (
        <ProDirectoryProfilePreviewFull
          data={{
            fullName: values.fullName,
            title: values.title,
            bio: values.bio,
            casualImage: values.casualImage,
            landscapeImage: values.landscapeImage,
            professionNames: professionOptions.map(p => String(p.name)),
            offerings: values.offerings ?? [],
            expertise: expertiseNames,
            languagesTaught: values.languagesTaught ?? [],
            teachingFormat: values.teachingFormat,
            certificates: values.certificates,
            companyName: values.companyName,
            companyEmail: values.companyEmail,
            professionalWebsite: values.professionalWebsite,
            phone: normalizePhone(values.phone),
            businessStreet: values.businessStreet,
            businessCity,
            businessState,
            businessCountry,
            socials: values.socials,
          }}
        />
      ) : (
        <SearchCardPreviewGrid
          data={{
            fullName: values.fullName,
            title: values.title,
            casualImage: values.casualImage,
            profession: professionOptions,
            expertise: expertiseNames.length > 0 ? expertiseNames : undefined,
            businessCity,
            businessState,
            businessCountry,
          }}
        />
      )}
    </Box>
  );
};
